import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  LayoutDashboard,
  Loader2,
  RefreshCw,
  Package,
  ClipboardList,
  Users,
  Ticket,
  MessageSquare,
  Truck,
  UserCog,
  FileCheck,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  Bell,
  DollarSign,
  BarChart3,
  ArrowRight,
  Clock,
  CheckCircle,
  XCircle,
  Building2,
  Calendar
} from "lucide-react";
import { useNavigate } from "react-router-dom";

// Period options for filtering
const PERIOD_OPTIONS = [
  { value: "hoy", label: "Hoy" },
  { value: "semana", label: "Esta Semana" },
  { value: "mes", label: "Este Mes" },
  { value: "trimestre", label: "Este Trimestre" }
];

// Module ID mapping to permissions
const MODULE_PERMISSION_MAP = {
  "inventario": "finanzas_contabilidad",
  "requisiciones": "requisiciones",
  "visitantes": "control_visitantes",
  "tickets": "mesa_ayuda",
  "whatsapp": "whatsapp",
  "mensajeria": "mensajeria",
  "rrhh": "gestion_humana",
  "comparaciones": "riesgos_generales"
};

const MODULES = [
  { 
    id: "inventario", 
    label: "Inventario", 
    icon: Package, 
    color: "from-blue-500 to-cyan-500",
    bgColor: "bg-blue-50",
    textColor: "text-blue-700",
    path: "/inventario"
  },
  { 
    id: "requisiciones", 
    label: "Requisiciones", 
    icon: ClipboardList, 
    color: "from-purple-500 to-pink-500",
    bgColor: "bg-purple-50",
    textColor: "text-purple-700",
    path: "/requisiciones"
  },
  { 
    id: "visitantes", 
    label: "Visitantes", 
    icon: Users, 
    color: "from-green-500 to-emerald-500",
    bgColor: "bg-green-50",
    textColor: "text-green-700",
    path: "/visitantes"
  },
  { 
    id: "tickets", 
    label: "Mesa de Ayuda", 
    icon: Ticket, 
    color: "from-orange-500 to-amber-500",
    bgColor: "bg-orange-50",
    textColor: "text-orange-700",
    path: "/mesa-ayuda/tickets"
  },
  { 
    id: "whatsapp", 
    label: "WhatsApp", 
    icon: MessageSquare, 
    color: "from-emerald-500 to-teal-500",
    bgColor: "bg-emerald-50",
    textColor: "text-emerald-700",
    path: "/whatsapp/bandeja"
  },
  { 
    id: "mensajeria", 
    label: "Mensajería", 
    icon: Truck, 
    color: "from-indigo-500 to-violet-500",
    bgColor: "bg-indigo-50",
    textColor: "text-indigo-700",
    path: "/mensajeria/solicitudes"
  },
  { 
    id: "rrhh", 
    label: "Recursos Humanos", 
    icon: UserCog, 
    color: "from-rose-500 to-pink-500",
    bgColor: "bg-rose-50",
    textColor: "text-rose-700",
    path: "/gestion-humana"
  },
  { 
    id: "comparaciones", 
    label: "Pólizas", 
    icon: FileCheck, 
    color: "from-slate-500 to-gray-600",
    bgColor: "bg-slate-50",
    textColor: "text-slate-700",
    path: "/comparar"
  }
];

const TrendIcon = ({ trend }) => {
  if (trend === "up") return <TrendingUp className="w-4 h-4 text-green-500" />;
  if (trend === "down") return <TrendingDown className="w-4 h-4 text-red-500" />;
  return <Minus className="w-4 h-4 text-slate-400" />;
};

const formatCurrency = (value) => {
  if (typeof value !== 'number') return 'RD$0.00';
  return `RD$${value.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export default function KPIDashboardPage() {
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState({});
  const [lastUpdate, setLastUpdate] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("mes");
  const [periodLabel, setPeriodLabel] = useState("Este Mes");
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();

  // Check if user has access to a specific module (same logic as MyDashboardPage)
  const hasModuleAccess = useCallback((moduleId) => {
    // Admin always has full access
    const userRole = user?.role?.toLowerCase();
    if (userRole === 'admin' || userRole === 'administrador') {
      return true;
    }
    
    // Get the permission key for this module
    const permissionKey = MODULE_PERMISSION_MAP[moduleId] || moduleId;
    
    if (!user?.menu_permissions) {
      return false;
    }
    
    const permission = user.menu_permissions[permissionKey];
    // If permission is explicitly set to true, allow access
    if (permission?.access === true) {
      return true;
    }
    // Default to false (no access)
    return false;
  }, [user]);

  const fetchKPIs = useCallback(async (period = selectedPeriod) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/dashboard/executive-kpis?periodo=${period}`, getAuthHeaders());
      setKpis(response.data.kpis || {});
      setPeriodLabel(response.data.periodo_label || "Este Mes");
      setLastUpdate(new Date());
    } catch (err) {
      console.error("Error fetching KPIs:", err);
      toast.error("Error al cargar los KPIs");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, selectedPeriod]);

  useEffect(() => {
    fetchKPIs(selectedPeriod);
    // Auto-refresh every 5 minutes
    const interval = setInterval(() => fetchKPIs(selectedPeriod), 300000);
    return () => clearInterval(interval);
  }, [selectedPeriod]);

  // Handle period change
  const handlePeriodChange = (newPeriod) => {
    setSelectedPeriod(newPeriod);
    fetchKPIs(newPeriod);
  };

  // Get alert count only for modules user has access to
  const getAlertCount = () => {
    let count = 0;
    if (hasModuleAccess('inventario') && kpis.inventario?.bajo_stock > 0) count += kpis.inventario.bajo_stock;
    if (hasModuleAccess('visitantes') && kpis.visitantes?.alertas_activas > 0) count += kpis.visitantes.alertas_activas;
    if (hasModuleAccess('tickets') && kpis.tickets?.urgentes > 0) count += kpis.tickets.urgentes;
    if (hasModuleAccess('whatsapp') && kpis.whatsapp?.no_leidos > 0) count += kpis.whatsapp.no_leidos;
    if (hasModuleAccess('rrhh') && kpis.rrhh?.contratos_por_vencer > 0) count += kpis.rrhh.contratos_por_vencer;
    return count;
  };

  const renderModuleCard = (module) => {
    const data = kpis[module.id] || {};
    const Icon = module.icon;
    
    return (
      <Card 
        key={module.id}
        className="hover:shadow-lg transition-all cursor-pointer group border-slate-200"
        onClick={() => navigate(module.path)}
        data-testid={`kpi-card-${module.id}`}
      >
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${module.color} flex items-center justify-center`}>
              <Icon className="w-5 h-5 text-white" />
            </div>
            <TrendIcon trend={data.tendencia} />
          </div>
          <CardTitle className="text-lg mt-2">{module.label}</CardTitle>
        </CardHeader>
        <CardContent>
          {data.error ? (
            <p className="text-sm text-red-500">Error al cargar datos</p>
          ) : (
            <div className="space-y-2">
              {module.id === "inventario" && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Artículos</span>
                    <span className="font-bold text-lg">{data.total_articulos || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Valor Total</span>
                    <span className="font-semibold text-blue-600">{formatCurrency(data.valor_inventario)}</span>
                  </div>
                  {data.bajo_stock > 0 && (
                    <Badge variant="destructive" className="mt-2">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      {data.bajo_stock} bajo stock
                    </Badge>
                  )}
                </>
              )}
              
              {module.id === "requisiciones" && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Total</span>
                    <span className="font-bold text-lg">{data.total || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Pendientes</span>
                    <Badge variant={data.pendientes > 0 ? "destructive" : "secondary"}>
                      {data.pendientes || 0}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Aprobadas ({periodLabel.toLowerCase()})</span>
                    <span className="text-green-600 font-medium">{data.aprobadas_periodo || 0}</span>
                  </div>
                </>
              )}
              
              {module.id === "visitantes" && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Hoy</span>
                    <span className="font-bold text-lg">{data.hoy || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">En edificio</span>
                    <Badge variant="outline" className="bg-green-50 text-green-700">
                      <Building2 className="w-3 h-3 mr-1" />
                      {data.en_edificio || 0}
                    </Badge>
                  </div>
                  {data.alertas_activas > 0 && (
                    <Badge variant="destructive" className="mt-2">
                      <Clock className="w-3 h-3 mr-1" />
                      {data.alertas_activas} tiempo excedido
                    </Badge>
                  )}
                </>
              )}
              
              {module.id === "tickets" && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Abiertos</span>
                    <Badge variant={data.abiertos > 5 ? "destructive" : "secondary"}>
                      {data.abiertos || 0}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Resueltos ({periodLabel.toLowerCase()})</span>
                    <span className="text-green-600 font-medium">{data.resueltos_periodo || 0}</span>
                  </div>
                  {data.urgentes > 0 && (
                    <Badge variant="destructive" className="mt-2">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      {data.urgentes} urgentes
                    </Badge>
                  )}
                </>
              )}
              
              {module.id === "whatsapp" && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Conversaciones</span>
                    <span className="font-bold text-lg">{data.conversaciones || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Mensajes hoy</span>
                    <span className="font-medium">{data.mensajes_hoy || 0}</span>
                  </div>
                  {data.no_leidos > 0 && (
                    <Badge variant="destructive" className="mt-2">
                      <Bell className="w-3 h-3 mr-1" />
                      {data.no_leidos} sin leer
                    </Badge>
                  )}
                </>
              )}
              
              {module.id === "mensajeria" && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Pendientes</span>
                    <Badge variant={data.pendientes > 0 ? "warning" : "secondary"} className={data.pendientes > 0 ? "bg-amber-100 text-amber-700" : ""}>
                      {data.pendientes || 0}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">En tránsito</span>
                    <span className="text-blue-600 font-medium">{data.en_transito || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Completadas ({periodLabel.toLowerCase()})</span>
                    <span className="text-green-600 font-medium">{data.completadas_periodo || 0}</span>
                  </div>
                </>
              )}
              
              {module.id === "rrhh" && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Empleados activos</span>
                    <span className="font-bold text-lg">{data.empleados_activos || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Vacaciones pendientes</span>
                    <Badge variant={data.vacaciones_pendientes > 0 ? "warning" : "secondary"} className={data.vacaciones_pendientes > 0 ? "bg-amber-100 text-amber-700" : ""}>
                      {data.vacaciones_pendientes || 0}
                    </Badge>
                  </div>
                  {data.contratos_por_vencer > 0 && (
                    <Badge variant="destructive" className="mt-2">
                      <Clock className="w-3 h-3 mr-1" />
                      {data.contratos_por_vencer} contratos por vencer
                    </Badge>
                  )}
                </>
              )}
              
              {module.id === "comparaciones" && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Total comparaciones</span>
                    <span className="font-bold text-lg">{data.total || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">{periodLabel}</span>
                    <span className="text-indigo-600 font-medium">{data.en_periodo || 0}</span>
                  </div>
                </>
              )}
              
              <Button 
                variant="ghost" 
                size="sm" 
                className="w-full mt-2 group-hover:bg-slate-100"
              >
                Ver detalles
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="kpi-dashboard-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-700 flex items-center justify-center shadow-lg">
              <BarChart3 className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
                Dashboard Ejecutivo
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                KPIs y métricas clave de todos los módulos
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Period Selector */}
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-500" />
              <Select value={selectedPeriod} onValueChange={handlePeriodChange}>
                <SelectTrigger className="w-[160px]" data-testid="period-selector">
                  <SelectValue placeholder="Seleccionar período" />
                </SelectTrigger>
                <SelectContent>
                  {PERIOD_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {lastUpdate && (
              <span className="text-sm text-slate-500">
                <Clock className="w-4 h-4 inline mr-1" />
                {lastUpdate.toLocaleTimeString('es-DO', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            <Button 
              variant="outline" 
              onClick={() => fetchKPIs(selectedPeriod)}
              disabled={loading}
              data-testid="refresh-kpis-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Actualizar
            </Button>
          </div>
        </div>

        {/* Period Badge */}
        <div className="mb-4">
          <Badge variant="outline" className="text-sm bg-indigo-50 text-indigo-700 border-indigo-200">
            <Calendar className="w-3 h-3 mr-1" />
            Mostrando datos de: {periodLabel}
          </Badge>
        </div>

        {/* Alert Summary - filtered by permissions */}
        {getAlertCount() > 0 && (
          <Card className="mb-6 border-red-200 bg-red-50/50">
            <CardContent className="py-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-red-800">
                    {getAlertCount()} alertas requieren atención
                  </h3>
                  <p className="text-sm text-red-600">
                    {hasModuleAccess('inventario') && kpis.inventario?.bajo_stock > 0 && `${kpis.inventario.bajo_stock} artículos bajo stock • `}
                    {hasModuleAccess('visitantes') && kpis.visitantes?.alertas_activas > 0 && `${kpis.visitantes.alertas_activas} visitantes tiempo excedido • `}
                    {hasModuleAccess('tickets') && kpis.tickets?.urgentes > 0 && `${kpis.tickets.urgentes} tickets urgentes • `}
                    {hasModuleAccess('whatsapp') && kpis.whatsapp?.no_leidos > 0 && `${kpis.whatsapp.no_leidos} mensajes sin leer`}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Stats - filtered by permissions */}
        <div className="kpi-carousel grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {hasModuleAccess('inventario') && (
            <Card className="bg-gradient-to-br from-blue-500 to-cyan-600 text-white border-0">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm">Valor Inventario</p>
                    <p className="text-2xl font-bold mt-1">
                      {formatCurrency(kpis.inventario?.valor_inventario || 0)}
                    </p>
                  </div>
                  <DollarSign className="w-10 h-10 text-blue-200" />
                </div>
              </CardContent>
            </Card>
          )}
          
          {hasModuleAccess('visitantes') && (
            <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100 text-sm">Visitantes Hoy</p>
                    <p className="text-2xl font-bold mt-1">{kpis.visitantes?.hoy || 0}</p>
                  </div>
                  <Users className="w-10 h-10 text-green-200" />
                </div>
              </CardContent>
            </Card>
          )}
          
          {hasModuleAccess('tickets') && (
            <Card className="bg-gradient-to-br from-orange-500 to-amber-600 text-white border-0">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-orange-100 text-sm">Tickets Abiertos</p>
                    <p className="text-2xl font-bold mt-1">{kpis.tickets?.abiertos || 0}</p>
                  </div>
                  <Ticket className="w-10 h-10 text-orange-200" />
                </div>
              </CardContent>
            </Card>
          )}
          
          {hasModuleAccess('rrhh') && (
            <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white border-0">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-100 text-sm">Empleados</p>
                    <p className="text-2xl font-bold mt-1">{kpis.rrhh?.empleados_activos || 0}</p>
                  </div>
                  <UserCog className="w-10 h-10 text-purple-200" />
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Module Cards - filtered by permissions */}
        {loading && !Object.keys(kpis).length ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-10 h-10 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {MODULES.filter(module => hasModuleAccess(module.id)).map(renderModuleCard)}
          </div>
        )}

        {/* Footer Info */}
        <div className="mt-8 text-center text-sm text-slate-400">
          <p>Los datos se actualizan automáticamente cada 5 minutos</p>
          <p className="mt-1">
            Bienvenido, <strong>{user?.name || user?.email}</strong>
          </p>
        </div>
      </main>
    </div>
  );
}
